'use client';

import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useMeasure, usePrevious, useWindowScroll } from 'react-use';

import { useSetGlobalCssVariable } from '@/src/hooks/useSetGlobalCssVariable';

import styles from './StickyAutoHideHeader.module.scss';

interface Props {
    children: ReactNode;
    className?: string;
}

const HEADER_HIDE_THRESHOLD = 700;

export function StickyAutoHideHeader({ children, className }: Props) {
    const scrollPosition = useWindowScroll();
    const prevScrollPosition = usePrevious(scrollPosition);
    const [headerMeasureRef, { height: headerHeight }] = useMeasure<HTMLDivElement>();

    const canBeHidden = scrollPosition.y > HEADER_HIDE_THRESHOLD;
    const isScrollingUp = prevScrollPosition && prevScrollPosition.y > scrollPosition.y;
    const isHeaderVisible = !canBeHidden || (canBeHidden && isScrollingUp);

    useSetGlobalCssVariable(
        '--header-sticky-height',
        isHeaderVisible ? `${headerHeight}px` : '0px',
    );

    return (
        <div
            className={classNames(className, {
                [styles.sticky]: scrollPosition.y > 0,
            })}
            style={{
                transform: isHeaderVisible ? `translateY(0)` : `translateY(-${headerHeight}px)`,
            }}
            ref={headerMeasureRef}
        >
            {children}
        </div>
    );
}
